import { gql } from '@apollo/client';
import {
  SLASentimentBarChartData,
  SLASentimentKeyInsightListData,
} from 'features/socialListeningAnalytics';
import {
  BrandInboundFiltersInputForSentimentMetrics,
  BrandSentimentCategoryBaseFiltersInput,
  GetBrandSentimentCategoriesSortBy,
  Sentiment,
  SentimentMedium,
  SortOrder,
  useGetBrandSentimentBreakdownForUseSlaBrandInboundSentimentViewDataQuery,
  useGetBrandSentimentCategoriesForUseSlaBrandInboundSentimentViewDataQuery,
} from 'graphql/generated';
import { KeyInsightItem } from 'pages/socialMediaListening/sentiment/sections/keyInsights/components';
import { useMemo } from 'react';
import { theme } from 'styles/theme';
import { getClaimMetricRandomEmoji } from './types';

export type GetSLABrandInboundSentimentViewDataProps = {
  filters: Omit<
    BrandInboundFiltersInputForSentimentMetrics,
    'sentiment' | 'medium'
  > & {
    dateRangeLabel?: string;
  };
  // effect to slaSentimentBarChartData
  medium: SentimentMedium;
  // effect to slaSentimentKeyInsightListData, return the list of key insights sorted by the selected sentiment
  sortBySentiment: Sentiment;
  dataOptions?: {
    // support maxKeyInsights to limit the number of key insights to show
    // if maxKeyInsights is not provided, show all key insights
    // if maxKeyInsights is provided, show the top maxKeyInsights key insights
    // if maxKeyInsights is provided, the key insights should be sorted by totalMentionCount in descending order
    maxKeyInsights?: number;
  };
};

// eslint-disable-next-line
gql`
  query GetBrandSentimentCategoriesForUseSLABrandInboundSentimentViewData(
    $filters: BrandSentimentCategoryBaseFiltersInput!
    $sortBy: GetBrandSentimentCategoriesSortByInput!
    $take: Int
    $after: String
  ) {
    getBrandSentimentCategories(
      filters: $filters
      sortBy: $sortBy
      take: $take
      after: $after
    ) {
      data {
        id
        createdAt
        updatedAt
        name
        totalMentions
        positiveMentions
        negativeMentions
        neutralMentions
        lastRefreshedAt
        generatedAnalysis
        brandId
        emoji
      }
      meta {
        totalCount
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
    }
  }
`;

// eslint-disable-next-line
gql`
  query GetBrandSentimentBreakdownForUseSLABrandInboundSentimentViewData(
    $filters: BrandSentimentCategoryBaseFiltersInput!
  ) {
    getBrandSentimentBreakdown(filters: $filters) {
      positiveRatio
      neutralRatio
      negativeRatio
    }
  }
`;

export type GetSLABrandInboundSentimentViewDataResult = {
  slaSentimentBarChartData: SLASentimentBarChartData;
  slaSentimentKeyInsightListData: SLASentimentKeyInsightListData;
};

export const useSLABrandInboundSentimentViewData = ({
  filters,
  medium,
  sortBySentiment,
  dataOptions,
}: GetSLABrandInboundSentimentViewDataProps) => {
  // parse filters to BrandSentimentCategoryBaseFiltersInput
  const brandSentimentCategoryFilters: BrandSentimentCategoryBaseFiltersInput =
    {
      brandId: filters.brandId,
      mediums: [medium],
      dateRange: filters.dateRange,
      countries: filters.countries,
      languages: filters.languages,
      sentiments: filters.sentiments,
      creatorIds: filters.creatorIds,
      postTypes: filters.postTypes,
      genders: filters.gender,
      generations: filters.generation,
      sources: filters.sources,
      platforms: filters.platforms,
    };

  const { data: brandSentimentBreakdownData } =
    useGetBrandSentimentBreakdownForUseSlaBrandInboundSentimentViewDataQuery({
      variables: {
        filters: brandSentimentCategoryFilters,
      },
    });

  // base on sortBySentiment
  const field: GetBrandSentimentCategoriesSortBy = useMemo(() => {
    switch (sortBySentiment) {
      case Sentiment.Positive:
        return GetBrandSentimentCategoriesSortBy.PositiveMentions;
      case Sentiment.Negative:
        return GetBrandSentimentCategoriesSortBy.NegativeMentions;
      case Sentiment.Neutral:
        return GetBrandSentimentCategoriesSortBy.NeutralMentions;
      default:
        return GetBrandSentimentCategoriesSortBy.PositiveMentions;
    }
  }, [sortBySentiment]);

  const { data: brandSentimentCategoriesData } =
    useGetBrandSentimentCategoriesForUseSlaBrandInboundSentimentViewDataQuery({
      variables: {
        filters: brandSentimentCategoryFilters,
        sortBy: {
          field,
          order: SortOrder.Desc,
        },
        take: 6,
      },
      fetchPolicy: 'cache-and-network',
    });

  const positiveRatio =
    brandSentimentBreakdownData?.getBrandSentimentBreakdown?.positiveRatio ?? 0;
  const negativeRatio =
    brandSentimentBreakdownData?.getBrandSentimentBreakdown?.negativeRatio ?? 0;
  const neutralRatio =
    brandSentimentBreakdownData?.getBrandSentimentBreakdown?.neutralRatio ?? 0;

  let keyInsightsFromBrandSentimentCategories: KeyInsightItem[] =
    useMemo(() => {
      return (
        brandSentimentCategoriesData?.getBrandSentimentCategories.data.map(
          (category) => {
            return {
              id: category.id,
              attribute: category.name,
              title: category.name,
              emoji: category.emoji ?? getClaimMetricRandomEmoji(0).emoji,
              emojiBgColor: getClaimMetricRandomEmoji(0).bgColor,
              totalMentionCount: category.totalMentions,
              generatedAnalysis: category.generatedAnalysis ?? '',
              sentiments: [
                {
                  sentiment: Sentiment.Positive,
                  commentCount: category.positiveMentions,
                },
                {
                  sentiment: Sentiment.Negative,
                  commentCount: category.negativeMentions,
                },
                {
                  sentiment: Sentiment.Neutral,
                  commentCount: category.neutralMentions,
                },
              ],
              brandSentimentCategory: category,
            };
          },
        ) ?? []
      );
    }, [brandSentimentCategoriesData, dataOptions?.maxKeyInsights]); // eslint-disable-line react-hooks/exhaustive-deps

  // if maxKeyInsights is provided, only get the top maxKeyInsights key insights
  if (dataOptions?.maxKeyInsights) {
    keyInsightsFromBrandSentimentCategories =
      keyInsightsFromBrandSentimentCategories.slice(
        0,
        dataOptions.maxKeyInsights,
      );
  }

  return {
    slaSentimentBarChartData: {
      chartData: [
        {
          sentiment: Sentiment.Positive,
          percent: positiveRatio * 100,
          color: theme.colors?.utility['green-1'],
        },
        {
          sentiment: Sentiment.Negative,
          percent: negativeRatio * 100,
          color: theme.colors?.utility['pink-1'],
        },
        {
          sentiment: Sentiment.Neutral,
          percent: neutralRatio * 100,
          color: theme.colors?.utility['blue-1'],
        },
      ],
    },
    slaSentimentKeyInsightListData: {
      keyInsights: keyInsightsFromBrandSentimentCategories,
    },
  };
};
